import React, { FC, useState, useCallback, useMemo } from "react";
import { Grid } from "@getregistered/greg-editors";
import type { RowAction } from "@getregistered/greg-editors/dist/lib/domain/grid/types";
import { gridLoader } from "../../domain/grid/resultLoader";
import { PaginatedApplicationPrimerGrid } from "./PaginatedApplicationPrimerGrid";
import { fetchJson } from "../railsFetch";
import { showToast } from "./gridToast";

interface EventRequestFieldGridProps {
  grid: Grid;
  resultsUrl: string;
  editUrl: string;
  deleteUrl: string;
  reorderUrl: string;
}

export const EventRequestFieldGrid: FC<EventRequestFieldGridProps> = ({
  grid,
  resultsUrl,
  editUrl,
  deleteUrl,
  reorderUrl,
}) => {
  const [resultsCacheKey, setResultsCacheKey] = useState(0);
  const loader = useMemo(() => gridLoader(resultsUrl, null, grid.columns), [resultsUrl, grid.columns]);

  const invalidateGrid = useCallback(() => {
    setResultsCacheKey((prev) => prev + 1);
  }, []);

  const handleEdit = useCallback((row: any) => {
    window.location.href = `${editUrl}/${row.id}/edit`;
  }, [editUrl]);

  const handleDelete = useCallback(async (row: any) => {
    if (!confirm("Are you sure you want to delete this field?")) return;

    try {
      const response = await fetchJson(`${deleteUrl}/${row.id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        showToast("Field deleted successfully", "success");
        invalidateGrid();
      } else if (response.status === 404) {
        // Already deleted elsewhere: refetch so the stale row goes.
        showToast("Failed to delete field", "danger");
        invalidateGrid();
      } else {
        try {
          const data = await response.json();
          showToast(data.errors?.join(", ") || "Failed to delete field", "danger");
        } catch {
          showToast("Failed to delete field", "danger");
        }
      }
    } catch {
      showToast("Failed to delete field", "danger");
    }
  }, [deleteUrl, invalidateGrid]);

  const handleReorder = useCallback(async (orderedIds: (string | number)[]) => {
    // Refetch on failure so the dragged row snaps back to the saved order,
    // as ReorderablePrimerGrid does.
    const failReorder = () => {
      showToast("Failed to reorder fields", "danger");
      invalidateGrid();
    };

    try {
      const response = await fetchJson(reorderUrl, {
        method: "PATCH",
        body: JSON.stringify({ ordered_ids: orderedIds }),
      });

      if (!response.ok) failReorder();
    } catch {
      failReorder();
    }
  }, [reorderUrl, invalidateGrid]);

  const rowActions: RowAction[] = [
    { title: "Edit", onClick: handleEdit },
    { title: "Delete", onClick: handleDelete },
  ];

  return (
    <PaginatedApplicationPrimerGrid
      grid={grid}
      loader={loader}
      defaultPerPage={100}
      perPageStorageKey="event_request_fields_per_page"
      page={1}
      reorderable={true}
      onReorder={handleReorder}
      rowActions={rowActions}
      showActions={true}
      actionCellRenderer="menu"
      showMetaCell={false}
      resultsCacheKey={resultsCacheKey}
    />
  );
};
